/**
 * CodeMirror Bookmark Extension
 *
 * Renders a bookmark icon directly inside the line-number gutter for any line
 * with a bookmark in the current file. Right-clicking on the line number opens
 * a context menu (handled by EditorPanel) for add/rename/remove. Provides
 * `Cmd+K Cmd+B` to toggle a bookmark on the cursor line.
 *
 * Subscribes to the `useBookmarkStore` and rebuilds markers reactively.
 */

import { Extension, RangeSet, RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import {
    EditorView,
    Decoration,
    DecorationSet,
    GutterMarker,
    gutter,
    gutterLineClass,
    keymap,
    ViewPlugin,
    ViewUpdate,
} from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";

import { useBookmarkStore } from "../../store/bookmark-store";
import {
    DRAG_HANDLE_ATTR,
    DRAG_HANDLE_CSS_CLASS,
} from "../codemirror-magic-syntax/drag-handle-gutter";
import { getMovableRanges } from "../codemirror-magic-syntax/movable-ranges";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

interface BookmarkLineMeta {
    id: string;
    line: number;
    label?: string;
    note?: string;
}

const setBookmarksEffect = StateEffect.define<Map<number, BookmarkLineMeta>>();

const enqueueBookmarkUpdate =
    typeof queueMicrotask === "function"
        ? queueMicrotask
        : (callback: () => void) => {
              void Promise.resolve().then(callback);
          };

const bookmarkLinesField = StateField.define<Map<number, BookmarkLineMeta>>({
    create: () => new Map(),
    update(value, tr) {
        for (const eff of tr.effects) {
            if (eff.is(setBookmarksEffect)) return eff.value;
        }
        return value;
    },
});

class BookmarkLineClassMarker extends GutterMarker {
    elementClass = "cm-bookmark-gutter-line";
}
const bookmarkLineClassMarker = new BookmarkLineClassMarker();

/** RangeSet derived from the bookmarks field, fed into `gutterLineClass`. */
const bookmarkRangeSetField = StateField.define<RangeSet<GutterMarker>>({
    create: () => RangeSet.empty,
    update(value, tr) {
        for (const eff of tr.effects) {
            if (eff.is(setBookmarksEffect)) {
                const map = eff.value;
                const builder = new RangeSetBuilder<GutterMarker>();
                const sortedLines = Array.from(map.keys()).sort((a, b) => a - b);
                for (const line of sortedLines) {
                    if (line < 1 || line > tr.state.doc.lines) continue;
                    const lineInfo = tr.state.doc.line(line);
                    builder.add(lineInfo.from, lineInfo.from, bookmarkLineClassMarker);
                }
                return builder.finish();
            }
        }
        return value;
    },
});

const bookmarkLineDecorationField = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(decos, tr) {
        decos = decos.map(tr.changes);
        for (const eff of tr.effects) {
            if (eff.is(setBookmarksEffect)) {
                const map = eff.value;
                const builder = new RangeSetBuilder<Decoration>();
                const sorted = Array.from(map.keys()).sort((a, b) => a - b);
                for (const line of sorted) {
                    if (line < 1 || line > tr.state.doc.lines) continue;
                    const lineInfo = tr.state.doc.line(line);
                    builder.add(
                        lineInfo.from,
                        lineInfo.from,
                        Decoration.line({ class: "cm-bookmark-line" }),
                    );
                }
                return builder.finish();
            }
        }
        return decos;
    },
    provide: (f) => EditorView.decorations.from(f),
});

const bookmarkTheme = EditorView.theme({
    ".cm-bookmark-ctx-gutter": {
        width: "16px",
        cursor: "context-menu",
    },
    ".cm-bookmark-spacer": {
        position: "relative",
        display: "inline-block",
        width: "16px",
        height: "1em",
    },
    ".cm-bookmark-dot": {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "16px",
        height: "100%",
        cursor: "context-menu",
    },
    ".cm-bookmark-dot::before": {
        content: '""',
        display: "block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: "#3b82f6",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.6)",
    },
    ".cm-bookmark-line": {
        backgroundColor: "rgba(59, 130, 246, 0.10)",
    },
    // ── Drag handle (rendered inside the same column as bookmarks) ──
    [`.${DRAG_HANDLE_CSS_CLASS}`]: {
        position: "absolute",
        inset: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "11px",
        lineHeight: "1",
        color: "var(--cm-foreground, #888)",
        opacity: "0",
        cursor: "grab",
        userSelect: "none",
        transition: "opacity 80ms ease-out",
    },
    [`.cm-bookmark-ctx-gutter:hover .${DRAG_HANDLE_CSS_CLASS}`]: {
        opacity: "0.55",
    },
    [`.cm-bookmark-ctx-gutter .${DRAG_HANDLE_CSS_CLASS}:hover`]: {
        opacity: "0.95",
    },
    [`.${DRAG_HANDLE_CSS_CLASS}:active`]: {
        cursor: "grabbing",
    },
}, { dark: false });

const bookmarkThemeDark = EditorView.theme({
    ".cm-bookmark-dot::before": {
        backgroundColor: "#60a5fa",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
    },
    ".cm-bookmark-line": {
        backgroundColor: "rgba(96, 165, 250, 0.15)",
    },
}, { dark: true });

interface BookmarkExtensionOptions {
    /** Returns the current file's absolute path; used to scope bookmark queries. */
    getFilePath: () => string | null;
}

export function magicBookmarks(options: BookmarkExtensionOptions): Extension {
    const { getFilePath } = options;

    function buildLineMap(): Map<number, BookmarkLineMeta> {
        const path = getFilePath();
        if (!path) return new Map();
        const items = useBookmarkStore.getState().getForPath(path);
        const map = new Map<number, BookmarkLineMeta>();
        for (const b of items) {
            map.set(b.line, { id: b.id, line: b.line, label: b.label, note: b.note });
        }
        return map;
    }

    /** Subscribes to store + path changes; pushes updates into state field. */
    const subscribePlugin = ViewPlugin.fromClass(
        class {
            unsub: () => void;
            lastPath: string | null;
            pending = false;
            destroyed = false;

            constructor(public view: EditorView) {
                this.lastPath = getFilePath();
                this.schedulePush();
                this.unsub = useBookmarkStore.subscribe(() => this.schedulePush());
            }
            update(_u: ViewUpdate) {
                const cur = getFilePath();
                if (cur !== this.lastPath) {
                    this.lastPath = cur;
                    this.schedulePush();
                }
            }
            schedulePush() {
                if (this.pending) return;
                this.pending = true;
                enqueueBookmarkUpdate(() => {
                    this.pending = false;
                    if (this.destroyed) return;
                    const map = buildLineMap();
                    this.view.dispatch({ effects: setBookmarksEffect.of(map) });
                });
            }
            destroy() {
                this.destroyed = true;
                this.unsub?.();
            }
        },
    );

    /**
     * Dedicated bookmark gutter sitting just to the left of the line numbers.
     * Renders a blue dot on bookmarked lines, otherwise a transparent spacer
     * (so contextmenu fires reliably for every line).
     */
    class BookmarkSpacerMarker extends GutterMarker {
        toDOM() {
            const el = document.createElement("span");
            el.className = "cm-bookmark-spacer";
            if (this.dragId) {
                const handle = document.createElement("span");
                handle.className = DRAG_HANDLE_CSS_CLASS;
                handle.setAttribute(DRAG_HANDLE_ATTR, this.dragId);
                handle.title = this.dragLabel
                    ? tr("syntax.widget.drag.moveNamed", { label: this.dragLabel })
                    : tr("syntax.widget.drag.move");
                handle.textContent = "⋮⋮";
                el.appendChild(handle);
            }
            return el;
        }
        constructor(public dragId: string | null = null, public dragLabel: string | null = null) {
            super();
        }
        eq(other: GutterMarker) {
            return (
                other instanceof BookmarkSpacerMarker &&
                other.dragId === this.dragId
            );
        }
    }
    class BookmarkDotMarker extends GutterMarker {
        toDOM() {
            const el = document.createElement("span");
            el.className = "cm-bookmark-dot";
            if (this.dragId) {
                const handle = document.createElement("span");
                handle.className = DRAG_HANDLE_CSS_CLASS;
                handle.setAttribute(DRAG_HANDLE_ATTR, this.dragId);
                handle.title = this.dragLabel
                    ? tr("syntax.widget.drag.moveNamed", { label: this.dragLabel })
                    : tr("syntax.widget.drag.move");
                handle.textContent = "⋮⋮";
                el.appendChild(handle);
            }
            return el;
        }
        constructor(public dragId: string | null = null, public dragLabel: string | null = null) {
            super();
        }
        eq(other: GutterMarker) {
            return (
                other instanceof BookmarkDotMarker &&
                other.dragId === this.dragId
            );
        }
    }
    const bookmarkSpacer = new BookmarkSpacerMarker();

    const bookmarkContextGutter = gutter({
        class: "cm-bookmark-ctx-gutter",
        initialSpacer: () => bookmarkSpacer,
        lineMarker: (view, line) => {
            const map = view.state.field(bookmarkLinesField, false);
            const lineNo = view.state.doc.lineAt(line.from).number;
            const movable = getMovableRanges(view.state).find(
                (r) => r.fromLine === lineNo,
            );
            const dragId = movable?.id ?? null;
            const dragLabel = movable?.label ?? null;
            const isBookmarked = map?.has(lineNo) ?? false;
            if (isBookmarked) return new BookmarkDotMarker(dragId, dragLabel);
            if (dragId) return new BookmarkSpacerMarker(dragId, dragLabel);
            return bookmarkSpacer;
        },
        lineMarkerChange: (update) => {
            if (update.docChanged) return true;
            for (const tr of update.transactions) {
                for (const eff of tr.effects) {
                    if (eff.is(setBookmarksEffect)) return true;
                }
            }
            return false;
        },
        domEventHandlers: {
            contextmenu(view, lineBlock, event) {
                event.preventDefault();
                event.stopPropagation();
                const line = view.state.doc.lineAt(lineBlock.from).number;
                const path = getFilePath();
                if (!path) return true;
                const existing = view.state.field(bookmarkLinesField, false)?.get(line);
                const me = event as MouseEvent;
                window.dispatchEvent(
                    new CustomEvent("magic-md:bookmark-menu", {
                        detail: {
                            x: me.clientX,
                            y: me.clientY,
                            path,
                            line,
                            existingId: existing?.id ?? null,
                            existingLabel: existing?.label ?? null,
                        },
                    }),
                );
                return true;
            },
        },
    });

    /** Keymap: Cmd/Ctrl+K Cmd/Ctrl+B → toggle bookmark on current line. */
    const bookmarkKeymap = keymap.of([
        {
            key: "Mod-k Mod-b",
            preventDefault: true,
            run(view) {
                const path = getFilePath();
                if (!path) return false;
                const line = view.state.doc.lineAt(view.state.selection.main.head).number;
                void useBookmarkStore.getState().toggle(path, line);
                return true;
            },
        },
    ]);

    return [
        bookmarkLinesField,
        bookmarkRangeSetField,
        bookmarkLineDecorationField,
        gutterLineClass.from(bookmarkRangeSetField),
        bookmarkContextGutter,
        subscribePlugin,
        bookmarkKeymap,
        bookmarkTheme,
        bookmarkThemeDark,
    ];
}
